import React from 'react';
import styled from '@emotion/styled';
import { DollarSign, TrendingUp, Clock, CheckCircle, RefreshCw } from 'lucide-react';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: #25253C;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  
  @media (max-width: 1200px) {
    padding: 16px;
    gap: 14px;
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
    border-radius: 8px;
    flex-direction: column;
    text-align: center;
  }
`;

const StatIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color || '#F43F60'};
  color: white;
  
  @media (max-width: 1200px) {
    width: 48px;
    height: 48px;
    border-radius: 10px;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }
`;

const StatContent = styled.div`
  flex: 1;
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const StatValue = styled.div`
  color: #ededed;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
  line-height: 1;
  
  @media (max-width: 1200px) {
    font-size: 22px;
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 2px;
  }
  
`;

const StatLabel = styled.div`
  color: #9E9E9E;
  font-size: 16px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 4px;
  }
`;

const StatChange = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  color: ${props => props.isPositive ? '#22C55E' : '#F43F60'};
  
  @media (max-width: 768px) {
    gap: 3px;
  }
  
  @media (max-width: 480px) {
    justify-content: center;
  }
`;

function OfferStats({ stats = {} }) {
    const statsData = [
        {
            icon: <DollarSign size={24} />,
            value: stats.totalOffers?.toString() || '0',
            label: 'Total Offers',
            change: '+15.2%',
            isPositive: true,
            color: '#F43F60'
        },
        {
            icon: <DollarSign size={24} />,
            value: `$${((stats.totalValue || 0) / 1000).toFixed(1)}K`,
            label: 'Total Value',
            change: '+8.7%',
            isPositive: true,
            color: '#22C55E'
        },
        {
            icon: <CheckCircle size={24} />,
            value: (stats.acceptedOffers || 0).toString(),
            label: 'Accepted Offers',
            change: '+12.3%',
            isPositive: true,
            color: '#8B5CF6'
        },
        {
            icon: <Clock size={24} />,
            value: (stats.pendingOffers || 0).toString(),
            label: 'Pending Offers',
            change: '+5.1%',
            isPositive: true,
            color: '#F59E0B'
        },
        {
            icon: <RefreshCw size={24} />,
            value: (stats.counteredOffers || 0).toString(),
            label: 'Countered Offers',
            change: '-2.8%',
            isPositive: false,
            color: '#EF4444'
        },
        {
            icon: <TrendingUp size={24} />,
            value: `$${(stats.averageOfferValue || 0).toFixed(0)}`,
            label: 'Average Offer Value',
            change: '+6.4%',
            isPositive: true,
            color: '#0FC9A7'
        }
    ];
    
    return (
        <StatsGrid>
            {statsData.map((stat, index) => (
                <StatCard key={index}>
                    <StatIcon color={stat.color}>
                        {stat.icon}
                    </StatIcon>
                    <StatContent>
                        <StatValue>{stat.value}</StatValue>
                        <StatLabel>{stat.label}</StatLabel>
                        <StatChange isPositive={stat.isPositive}>
                            {stat.isPositive ? <TrendingUp size={14} /> : <TrendingUp size={14} style={{ transform: 'rotate(180deg)' }} />}
                            {stat.change}
                        </StatChange>
                    </StatContent>
                </StatCard>
            ))}
        </StatsGrid>
    );
}

export default OfferStats;  